/**
 * Bootstrap Provider - App Initialization (Optimized)
 *
 * Uses a 2-phase loading strategy for faster initial render:
 *
 * Phase 1 - Critical (blocks render for HOME PAGE ONLY):
 * - User profile and permissions
 * - Presence status
 * - Team presence
 * - Notifications
 * - My work summary
 * - CS dashboard (managers)
 *
 * Phase 2 - Deferred (background after render):
 * - Chat sessions and folders
 * - Commands and templates
 * - Pending team invites
 *
 * PERFORMANCE FIX (Jan 2026):
 * Bootstrap data is only needed by the home page (ConsoleLayout).
 * Other pages (/cs, /crm, /projects, etc.) load their own data.
 * We only block rendering for the home page - other routes render immediately
 * while bootstrap loads in the background, eliminating 3-30 second delays.
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { queryKeys } from '@/lib/queryKeys';
import { config } from '@/lib/config';
import type { BootstrapData } from '@/hooks/useBootstrap';

const API_BASE = config.apiUrl;

// Type for critical bootstrap response
interface CriticalBootstrapData {
  user: BootstrapData['user'];
  presence: BootstrapData['presence'];
  team: BootstrapData['team'];
  notifications: BootstrapData['notifications'];
  my_work?: BootstrapData['my_work'];
  cs_dashboard?: BootstrapData['cs_dashboard'];
  timestamp: string;
  cache_key: string;
}

// Type for deferred bootstrap response
interface DeferredBootstrapData {
  commands: BootstrapData['commands'];
  templates: BootstrapData['templates'];
  sessions: BootstrapData['sessions'];
  session_folders: BootstrapData['session_folders'];
  pending_invites: BootstrapData['pending_invites'];
  timestamp: string;
}

interface BootstrapContextValue {
  isReady: boolean;
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

const BootstrapContext = createContext<BootstrapContextValue>({
  isReady: false,
  isLoading: false,
  error: null,
  refresh: async () => {},
});

export function useBootstrapContext() {
  return useContext(BootstrapContext);
}

interface BootstrapProviderProps {
  children: React.ReactNode;
}

export function BootstrapProvider({ children }: BootstrapProviderProps) {
  const { user, getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Track if deferred data has been loaded to avoid duplicate requests
  const deferredLoadedRef = useRef(false);

  // Seed critical data into React Query cache
  const seedCriticalData = useCallback((data: CriticalBootstrapData) => {
    // User data
    queryClient.setQueryData(queryKeys.user.me(), data.user);
    queryClient.setQueryData(queryKeys.user.permissions(), data.user.permissions);

    // Presence data (seed combined presence format)
    queryClient.setQueryData(queryKeys.presence.combined(), {
      my_status: data.presence,
      team: data.team,
      blockers: [], // Will be fetched separately if needed
      pending_invites: [], // Will be loaded in deferred
    });

    // Individual presence keys for backwards compatibility
    queryClient.setQueryData(queryKeys.presence.status(), data.presence);
    queryClient.setQueryData(queryKeys.presence.team(), data.team);

    // Notifications
    queryClient.setQueryData(queryKeys.notifications.all, data.notifications);
    queryClient.setQueryData(queryKeys.notifications.unreadCount(), data.notifications.unread_count);

    // My work summary
    if (data.my_work) {
      queryClient.setQueryData(queryKeys.projects.myWork(), data.my_work);
      queryClient.setQueryData(queryKeys.projects.myDay(), data.my_work);
    }

    // CS dashboard summary (managers only)
    if (data.cs_dashboard) {
      queryClient.setQueryData(queryKeys.customerSuccess.dashboard(), data.cs_dashboard);
    }
  }, [queryClient]);

  // Seed deferred data into React Query cache
  const seedDeferredData = useCallback((data: DeferredBootstrapData) => {
    // Commands and templates
    queryClient.setQueryData(queryKeys.commands.list(), data.commands);
    queryClient.setQueryData(queryKeys.templates.list(), data.templates);

    // Chat sessions and folders - convert API sessions to proper Session type with Date objects
    if (data.sessions) {
      const convertedSessions = data.sessions.map(s => ({
        id: s.id,
        title: s.title,
        createdAt: new Date(s.created_at),
        updatedAt: new Date(s.updated_at),
        activeCompany: s.company_context,
        isPinned: s.is_pinned ?? false,
        folderId: s.folder_id ?? undefined,
        lastActivityAt: s.last_activity_at ? new Date(s.last_activity_at) : undefined,
        clientTag: s.client_tag,
        fathomMeetingCount: s.fathom_meeting_count,
        actionItemsCount: s.action_items_count,
        projectTags: s.project_tags,
        docLinks: s.doc_links,
        apiCallCount: s.api_call_count,
      }));
      queryClient.setQueryData(queryKeys.sessions.lists(), convertedSessions);
    }
    if (data.session_folders) {
      queryClient.setQueryData(queryKeys.sessions.folders(), data.session_folders);
    }

    // Pending team invites
    if (data.pending_invites) {
      queryClient.setQueryData(queryKeys.teamHub.pendingInvites(), data.pending_invites);
      // Update combined presence with pending invites
      const currentCombined = queryClient.getQueryData(queryKeys.presence.combined()) as {
        my_status: unknown;
        team: unknown;
        blockers: unknown[];
        pending_invites: unknown[];
      } | undefined;
      if (currentCombined) {
        queryClient.setQueryData(queryKeys.presence.combined(), {
          ...currentCombined,
          pending_invites: data.pending_invites,
        });
      }
    }
  }, [queryClient]);

  // Fetch deferred data in background (after initial render)
  const fetchDeferredData = useCallback(async (token: string) => {
    if (deferredLoadedRef.current) return;

    try {
      const response = await fetch(`${API_BASE}/api/init/bootstrap/deferred`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.warn('[Bootstrap] Deferred data fetch failed, will retry on next interaction');
        return;
      }

      const data: DeferredBootstrapData = await response.json();
      seedDeferredData(data);
      deferredLoadedRef.current = true;

      console.log('[Bootstrap] Seeded deferred cache with:', {
        sessions: data.sessions?.length || 0,
        folders: data.session_folders?.length || 0,
        commands: data.commands?.length || 0,
        templates: data.templates?.length || 0,
        pendingInvites: data.pending_invites?.length || 0,
      });
    } catch (err) {
      console.warn('[Bootstrap] Deferred data fetch error:', err);
    }
  }, [seedDeferredData]);

  // Fetch critical bootstrap data and seed caches
  const fetchAndSeedBootstrap = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();

      // Phase 1: Fetch critical data (blocks render)
      const criticalResponse = await fetch(`${API_BASE}/api/init/bootstrap/critical`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!criticalResponse.ok) {
        const err = await criticalResponse.json().catch(() => ({ error: 'Bootstrap failed' }));
        throw new Error(err.detail || err.error || 'Failed to fetch bootstrap data');
      }

      const criticalData: CriticalBootstrapData = await criticalResponse.json();
      seedCriticalData(criticalData);

      console.log('[Bootstrap] Critical data loaded - user, presence, notifications, my_work');

      // Mark as ready immediately after critical data
      setIsReady(true);
      setIsLoading(false);

      // Phase 2: Fetch deferred data in background (doesn't block render)
      // Use requestIdleCallback or setTimeout to defer
      if ('requestIdleCallback' in window) {
        (window as Window & { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(() => {
          fetchDeferredData(token);
        });
      } else {
        setTimeout(() => {
          fetchDeferredData(token);
        }, 100);
      }

    } catch (err) {
      console.error('[Bootstrap] Failed to load critical data:', err);
      setError(err instanceof Error ? err : new Error('Bootstrap failed'));
      // Still mark as ready so app can render (with fallback states)
      setIsReady(true);
      setIsLoading(false);
    }
  }, [user, getAccessToken, seedCriticalData, fetchDeferredData]);

  // Full refresh (for manual refresh button)
  const refreshBootstrap = useCallback(async () => {
    deferredLoadedRef.current = false;
    await fetchAndSeedBootstrap();
  }, [fetchAndSeedBootstrap]);

  // Run bootstrap on mount when user is authenticated
  useEffect(() => {
    if (user && !isReady && !isLoading) {
      fetchAndSeedBootstrap();
    }
  }, [user]);

  // Reset on logout
  useEffect(() => {
    if (!user) {
      setIsReady(false);
      setError(null);
      deferredLoadedRef.current = false;
    }
  }, [user]);

  const value: BootstrapContextValue = {
    isReady: !user || isReady, // Ready if no user (login page) or bootstrap done
    isLoading,
    error,
    refresh: refreshBootstrap,
  };

  // PERFORMANCE FIX: Only block rendering for routes that actually need bootstrap data.
  // The home page (ConsoleLayout) needs bootstrap data for presence, team, notifications, etc.
  // Other pages (/cs, /crm, /projects, etc.) load their own data and don't need to wait.
  // This eliminates 3-30 second delays on non-home page refreshes.
  const shouldBlockRendering = () => {
    const path = window.location.pathname;
    // Only block for home page - all other routes can render immediately
    // Bootstrap will still load in background and seed React Query cache
    return path === '/' || path === '/index' || path === '/index.html';
  };

  // Block rendering until bootstrap is ready (only for home page)
  // Other routes render immediately - bootstrap loads in background
  if (user && !isReady && shouldBlockRendering()) {
    return (
      <BootstrapContext.Provider value={value}>
        <div className="flex items-center justify-center h-screen bg-background">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="text-sm text-muted-foreground">Loading...</span>
          </div>
        </div>
      </BootstrapContext.Provider>
    );
  }

  return (
    <BootstrapContext.Provider value={value}>
      {children}
    </BootstrapContext.Provider>
  );
}

export default BootstrapProvider;
